




























































































import DateField from '@/components/FormBuilder/fields/DateField.vue';
import AccountingSheetController from '@/controllers/AccountingSheet';
import { UpdateAccountingSheetData } from '@/controllers/types';
import {
  Component, Mixins, Prop,
} from 'vue-property-decorator';
import { required } from 'vuelidate/lib/validators';
import { Validations } from 'vuelidate-property-decorators';
import { validationMixin } from 'vuelidate';
import { AccountingSheetInterface } from '@/types/AccountingSheet';
import UserMixin from '@/mixins/UserMixin';
import {
  calcTaxAmountByRate, isTaxAmountDeviating, normalizeTaxAmount, taxAmountFormat, taxRateFormat,
} from '@/utils/taxAmount';
import { describeOperationError } from '@/utils/extractApiErrorMessage';

@Component({
  components: {
    DateField,
  },
})
export default class ErpData extends Mixins(validationMixin, UserMixin) {
  @Prop()
  readonly accountingSheet!: AccountingSheetInterface;

  isSavingErpData = false;

  showCopyText = false;

  // Ставка НДС, % (целое 0–100). Хранится отдельно от erpData, чтобы не попадать в общий v-for.
  taxRate: string | number | null = null;

  // Слать tax в payload только если пользователь его менял (задача 8045)
  taxRateEdited = false;

  // Значения, с которыми форма открылась. Нужны, чтобы отправлять только
  // изменённые поля (задача 10333): история обработки листа перечисляет
  // пришедшие поля, и форма целиком превращала правку одной суммы в
  // «изменены все реквизиты».
  initialErpData: UpdateAccountingSheetData | null = null;

  erpData = {
    // дата счета на документе от поставщика, бух проверяет, но не заполняет : автоматом или контролинг
    doc_date: '',
    // дата проведения документа в рамках открытого периода - должен вносить бух, автоматизировать не нужно
    post_date: '',
    // № счет-фактуры дата счета на документе от поставщика, бух проверяет, но не заполняет : автоматом или контролинг
    invoice_number: '',
    // № счета дата счета на документе от поставщика, бух проверяет, но не заполняет : автоматом или контролинг
    account_number: '',
    // Итого НДС
    total_tax: 0,
    for_copying: '',
  }

  labelErpData = {
    doc_date: 'Дата Счет-фактуры или УПД или Акта',
    post_date: 'Дата проведения документа',
    invoice_number: '№ Счет-фактуры или УПД или Акта',
    account_number: '№ счета',
    total_tax: 'Итого НДС',
  }

  beforeMount() {
    this.fillErpData(this.accountingSheet);
  }

  @Validations()
  validations() {
    return {
      erpData: {
        doc_date: {
          required,
        },
        post_date: {
          required,
        },
        invoice_number: {
          required,
        },
        account_number: {
          required,
        },
        total_tax: {
          taxAmountFormat,
        },
      },
      taxRate: {
        taxRateFormat,
      },
    };
  }

  get isControllingApprovalStatus(): boolean {
    return this.accountingSheet !== null && this.accountingSheet.status === 'controlling_approval';
  }

  get isAwaitSigningStatus(): boolean {
    return this.accountingSheet !== null && this.accountingSheet.status === 'diadoc_signing_await';
  }

  get isAccountingApprovalStatus(): boolean {
    return this.accountingSheet !== null && this.accountingSheet.status === 'accounting_approval';
  }

  get isStatusWithAction(): boolean {
    return this.isControllingApprovalStatus || this.isAwaitSigningStatus || this.isAccountingApprovalStatus;
  }

  get normalizedTotalTax(): number {
    return normalizeTaxAmount(this.erpData.total_tax);
  }

  // Ставка редактируема ТОЛЬКО бухгалтером и только в статусах с действием
  get isTaxRateEditable(): boolean {
    return this.isAccountant && this.isStatusWithAction;
  }

  // Расчётная сумма НДС по ставке; null, если ставка пуста или невалидна
  get expectedTaxAmount(): number | null {
    const rate = this.taxRate;
    if (rate === null || String(rate).trim() === '' || !taxRateFormat(rate)) return null;
    return calcTaxAmountByRate(Number(this.accountingSheet?.totalWithoutTax) || 0, Number(rate));
  }

  // Неблокирующее предупреждение: расхождение суммы с расчётной по ставке > 1%
  get taxAmountWarnings(): Array<string> {
    if (this.expectedTaxAmount === null) return [];
    if (isTaxAmountDeviating(this.normalizedTotalTax, this.expectedTaxAmount)) {
      return [`Сумма отличается от расчётной по ставке (${this.expectedTaxAmount.toFixed(2)}) более чем на 1%`];
    }
    return [];
  }

  // Изменение ставки → пересчёт «Итого НДС»; ручное изменение суммы ставку не меняет
  onTaxRateInput(): void {
    this.taxRateEdited = true;
    if (this.expectedTaxAmount !== null) {
      this.erpData.total_tax = this.expectedTaxAmount;
    }
  }

  onDateChange(value: string, key: string): void {
    Object.assign(this.erpData, { [key]: value });
  }

  copyText() {
    const element = this.$refs.for_copying;
    (element as any).select();
    document.execCommand('copy');
    this.showCopyText = true;
    setTimeout(() => {
      this.showCopyText = false;
    }, 3000);
  }

  fillErpData(accountingSheet: AccountingSheetInterface): void {
    if (accountingSheet) {
      this.erpData.doc_date = accountingSheet.accountDate || '';
      this.erpData.post_date = accountingSheet.documentDate || '';
      this.erpData.invoice_number = accountingSheet.invoiceNumber || '';
      this.erpData.account_number = accountingSheet.accountNumber || '';
      this.erpData.total_tax = accountingSheet.taxAmount || 0;
      this.taxRate = typeof accountingSheet.tax === 'number' ? accountingSheet.tax : null;

      const formattedNumber = accountingSheet.number.replace(/^\D+/g, '');
      let formattedFinishDate = '';
      const finishDate = accountingSheet.finishDate ? new Date(accountingSheet.finishDate) : null;
      if (finishDate !== null) {
        finishDate.setDate(finishDate.getDate() + accountingSheet.contractPaymentPeriod2);
        formattedFinishDate = finishDate.toLocaleDateString();
      }
      const formattedTotal = accountingSheet.total.toFixed(2);
      this.erpData.for_copying = `${formattedNumber}_${accountingSheet.fmLegalCode}_${formattedFinishDate}_${formattedTotal}`;
      this.initialErpData = this.collectFormData();
    }
  }

  // Поля формы в том же виде, в каком они уходят в запрос: снимок и текущее
  // состояние должны сравниваться один к одному, без приведения типов.
  collectFormData(): UpdateAccountingSheetData {
    return {
      sheetId: this.accountingSheet.id,
      documentDate: String(this.erpData.post_date),
      accountDate: String(this.erpData.doc_date),
      invoiceNumber: String(this.erpData.invoice_number),
      accountNumber: String(this.erpData.account_number),
      taxAmount: this.normalizedTotalTax,
    };
  }

  getValidationErrors(validations: any): Array<string> {
    const errors: Array<string> = [];
    if (!validations || validations.$error === false || validations.$invalid === false) {
      return errors;
    }
    if (validations.required === false) {
      errors.push('Обязательное поле');
    }
    if (validations.taxAmountFormat === false) {
      errors.push('Введите неотрицательное число, не более 2 знаков после запятой');
    }
    if (validations.taxRateFormat === false) {
      errors.push('Введите целое число от 0 до 100');
    }
    return errors;
  }

  getPreparedData(): UpdateAccountingSheetData {
    const current = this.collectFormData();
    const initial = this.initialErpData;
    let data: UpdateAccountingSheetData = { sheetId: this.accountingSheet.id };

    const textFields: Array<'documentDate' | 'accountDate' | 'invoiceNumber' | 'accountNumber'> = [
      'documentDate', 'accountDate', 'invoiceNumber', 'accountNumber',
    ];
    textFields.forEach((field) => {
      // Снимка нет — форма ещё не заполнялась; шлём поле, чтобы правка не потерялась.
      if (initial === null || current[field] !== initial[field]) {
        data = { ...data, [field]: current[field] };
      }
    });

    // Ноль в «Итого НДС» сервис не принимает как значение, поэтому шлём сумму
    // только когда она непустая и отличается от той, что была при открытии.
    if (this.normalizedTotalTax && (initial === null || this.normalizedTotalTax !== initial.taxAmount)) {
      data = { ...data, taxAmount: this.normalizedTotalTax };
    }
    // tax шлём только если пользователь менял ставку и роль позволяет её редактировать
    if (this.taxRateEdited && this.isTaxRateEditable && this.taxRate !== null && String(this.taxRate).trim() !== '' && taxRateFormat(this.taxRate)) {
      data = { ...data, tax: Number(this.taxRate) };
    }
    return data;
  }

  // Кроме sheetId в теле остаётся только изменённое, поэтому пустое тело —
  // это «сохранять нечего». Сервис на таком запросе ответил бы 400.
  hasErpChanges(data: UpdateAccountingSheetData): boolean {
    return Object.keys(data).some((key) => key !== 'sheetId');
  }

  async onSubmit(silence = false): Promise<boolean> {
    let answer = false;
    if (!this.isStatusWithAction) {
      this.$bus.$emit('notification', 'Статус листа учета не позволяет сохранить данные ERP', 'warning');
      return answer;
    }
    try {
      this.$v.$touch();
      if ((this.$v.erpData as any).total_tax.$invalid) return answer;
      if ((this.$v as any).taxRate.$invalid) return answer;
      if (this.$v.$invalid && !this.isControllingApprovalStatus) return answer;
      const preparedData = this.getPreparedData();
      // Ничего не тронули — сохранять нечего. Возвращаем успех: onSubmit(true)
      // вызывается перед подтверждением листа бухгалтером, и отказ здесь сорвал
      // бы подтверждение из-за того, что реквизиты и так верны.
      if (!this.hasErpChanges(preparedData)) {
        if (!silence) this.$bus.$emit('notification', 'Данные ERP не изменялись', 'info');
        return true;
      }
      this.isSavingErpData = true;
      const response = await AccountingSheetController.update(preparedData);
      const [error, result] = response.result;
      // Ошибку разбираем до result: на этой ветке result всегда null (Task.execute),
      // и обращение к result.result роняло TypeError в catch ниже — пользователь
      // видел «TypeError:» вместо причины отказа.
      if (error) {
        // 4xx — это объяснимый отказ, а не сбой: сервис прислал готовый русский
        // текст (409 «лист уже отправлен в ERP», 403 про ставку НДС), и «попробуйте
        // позже» к нему не подходит — позже будет ровно то же самое.
        const isClientError = response.code >= 400 && response.code < 500;
        const message = isClientError
          ? String(error)
          : `Возникла ошибка при сохранении данных ERP: ${describeOperationError(error)}`;
        this.$bus.$emit('notification', message, 'error');
        console.error(error);
        return answer;
      }
      if (result?.result) {
        console.log('Result after saving ERP data - ', result);
        if (!silence) this.$bus.$emit('notification', 'Данные ERP успешно сохранены', 'success');
        // Сохранённое становится новой точкой отсчёта: без этого повторное
        // «Сохранить» отправило бы те же поля ещё раз и завело в историю листа
        // правку, которой не было.
        this.initialErpData = this.collectFormData();
        this.taxRateEdited = false;
        answer = true;
      }
    } catch (error) {
      this.$bus.$emit('notification', `Возникла ошибка при сохранении данных ERP: ${describeOperationError(error)}`, 'error');
      console.error(error);
    } finally {
      this.isSavingErpData = false;
    }
    return answer;
  }
}
